import PrintableText from './PrintableText';
import PrintableHtml from './PrintableHtml';

export default class Printable {

    constructor() {
        this.printableContent = []
    }

    /**
     * Adds a line of text to the printable content.
     *
     * @param {String} type - The type of line to be added.
     * @param  {...any} args - The content to be printed. This can be a variable number of arguments.
     *                         The last argument can optionally be an object defining options for the line.
     */
    l(type, ...args) {
        // Initialize options as an empty object
        let options = {};

        // Check if the last argument is an options object
        if (typeof args[args.length - 1] === 'object' && args[args.length - 1] !== null && !Array.isArray(args[args.length - 1])) {
            options = args.pop();
        }

        this.printableContent.push({
            type,
            data: [
                { content: args.map(a => '' + a), ...options }
            ],
        })
    }

    /**
     * Adds a table line of text to the printable content.
     *
     * @param  {...any} args - Each argument should be an object containing the content of one cell and other options like colSpan, align, style, etc.
     */
    tl(...args) {
        const tableData = this.printableContent[this.printableContent.length - 1];
        if(tableData.type !== 'T') return

        args = args.map(arg => {
            if (!Array.isArray(arg.content)) {
                arg.content = [arg.content];
            }

            arg.content = arg.content.map(c => '' + c)

            return arg
        })

        tableData.data = [...tableData.data, ...args]
    }

    /**
     * Creates a table with the given options and adds it to the printable content. Example:
     *
     * {
     *   colNumber: 4,
     *   text: {
     *     colWidths: [25, 25, 25, 25],
     *   },
     *   html: {
     *     colWidths: ['100px', null, '12.5%', '12.5%'],
     *     style: { background: 'red' },
     *   }
     * }
     *
     * @param {Object} options - The options for the table.
     * @param {Number} [options.colNumber=2] - The number of columns in the table. Defaults to 2 if not provided.
     * @param {Object} [options.text={}] - An object specifying the text settings for the table. Can contain colWidths array.
     * @param {Object} [options.html={}] - An object specifying the styles for the table. Can contain colWidths array, and style object.
     */
    table(options) {
        const { colNumber = 2, text = {}, html = {} } = options

        this.printableContent.push({
            type: 'T',
            data: [],
            colNumber,
            text,
            html,
        })
    }

    /**
     * Add to the printable content
     */
    addPrintableContent(printableContent) {
        printableContent.map(pc => {
            pc.data = pc.data.map(d => {
                d.content = d.content.map(c => '' + c)
                return d
            })
            return pc
        })

        this.printableContent = [...this.printableContent, ...printableContent]
    }

    /**
     * Retrieves the printable content currently stored in the Printable object.
     *
     * @returns {Array} The array of printable content.
     */
    getPrintableContent() {
        return this.printableContent
    }

    /**
     * Converts the printable content to a text format.
     *
     * @param {Number} [charsPerLine=32] - The maximum number of characters per line. Defaults to 32 if not provided.
     * @returns {String} The printable content in text format.
     */
    toText(charsPerLine = 32) {
        const printableText = new PrintableText(this.printableContent, charsPerLine)
        return printableText.process()
    }

    /**
     * Converts the printable content to HTML format.
     *
     * @returns {String} The printable content in HTML format.
     */
    toHtml(wrapperClass = null) {
        const printableHtml = new PrintableHtml(this.printableContent, wrapperClass)
        return printableHtml.process()
    }
}
